import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { PaziaButton, PaziaInput } from "components/atoms";
import { settingsApi } from "api";
import { useInvalidateSettings } from "api/hooks/useInvalidateSettings";
import { getApiErrorMessage, useToaster } from "utils";

export interface ClientAppSettingsFormProps {
  hostName: string;
  trainerAppHostName: string;
  resetPasswordPage: string;
  loginPage: string;
  coursePaymentsPage: string;
  courseSchedulePage: string;
  courseExamsPage: string;
  settingsPage: string;
}

const LINKS_TOOLTIP =
  "Linki w e-mailach są budowane z adresu aplikacji klienta i ścieżki strony, np. https://pazia.pl + strefa-klienta/kursy/{courseId}?tab=payments.";
const COURSE_PAGE_TOOLTIP = "Ścieżka musi zawierać {courseId}, w to miejsce trafia identyfikator kursu.";

export const ClientAppSettingsForm = (props: ClientAppSettingsFormProps) => {
  const { showError, showSuccess } = useToaster();
  const invalidateSettings = useInvalidateSettings();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: props,
  });

  const onSubmit = async (values: ClientAppSettingsValues) => {
    try {
      // Sent as-is: an empty trainerAppHostName clears the trainer panel address.
      await settingsApi.saveClientApp(values);
      await invalidateSettings();
      methods.reset(values);
      showSuccess("Zapisano adresy aplikacji");
    } catch (error) {
      showError(getApiErrorMessage(error, "Błąd podczas zapisywania adresów aplikacji"));
    }
  };

  const {
    handleSubmit,
    formState: { isDirty, errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
        <PaziaInput
          label="Adres aplikacji klienta"
          name="hostName"
          placeholder="https://pazia.pl"
          tooltip={`Adres strony dla kursantów, z https:// i bez ukośnika na końcu. ${LINKS_TOOLTIP}`}
        />
        <PaziaInput
          label="Adres panelu trenera"
          name="trainerAppHostName"
          placeholder="https://trener.pazia.pl"
          tooltip="Adres panelu trenera, np. https://trener.pazia.pl, z https:// i bez ukośnika na końcu. Służy do budowania linków w e-mailach do trenerów i jest wymagany, aby te linki (np. reset hasła) działały."
        />
        <PaziaInput
          label="Strona resetu hasła"
          name="resetPasswordPage"
          tooltip={`Ścieżka strony resetu hasła. ${LINKS_TOOLTIP}`}
        />
        <PaziaInput label="Strona logowania" name="loginPage" tooltip={`Ścieżka strony logowania. ${LINKS_TOOLTIP}`} />
        <PaziaInput
          label="Strona płatności kursu"
          name="coursePaymentsPage"
          placeholder="strefa-klienta/kursy/{courseId}?tab=payments"
          tooltip={COURSE_PAGE_TOOLTIP}
        />
        <PaziaInput
          label="Strona harmonogramu kursu"
          name="courseSchedulePage"
          placeholder="strefa-klienta/kursy/{courseId}?tab=schedule"
          tooltip={COURSE_PAGE_TOOLTIP}
        />
        <PaziaInput
          label="Strona egzaminów kursu"
          name="courseExamsPage"
          placeholder="strefa-klienta/kursy/{courseId}?tab=exams"
          tooltip={COURSE_PAGE_TOOLTIP}
        />
        <PaziaInput
          label="Strona ustawień konta"
          name="settingsPage"
          placeholder="ustawienia"
          tooltip={`Ścieżka strony ustawień konta kursanta. ${LINKS_TOOLTIP}`}
        />
        <div>
          <PaziaButton
            type="success"
            disabled={!isDirty}
            title="Zapisz"
            className={Object.keys(errors).length ? "mb-[26px]" : ""}
          />
        </div>
      </form>
    </FormProvider>
  );
};

const HTTPS_PREFIX = /^https:\/\/\S+$/;
const HTTPS_MESSAGE = "Adres musi zaczynać się od https:// i nie może zawierać spacji";
const TRAILING_SLASH_MESSAGE = "Adres nie może kończyć się ukośnikiem /";
const COURSE_ID_MESSAGE = "Ścieżka musi zawierać {courseId}";

const noTrailingSlash = (value?: string) => !value || !value.endsWith("/");
const containsCourseId = (value?: string) => (value ?? "").includes("{courseId}");

const schema = yup
  .object({
    hostName: yup
      .string()
      .trim()
      .required()
      .matches(HTTPS_PREFIX, HTTPS_MESSAGE)
      .test("no-trailing-slash", TRAILING_SLASH_MESSAGE, noTrailingSlash),
    trainerAppHostName: yup
      .string()
      .trim()
      .default("")
      .matches(HTTPS_PREFIX, { message: HTTPS_MESSAGE, excludeEmptyString: true })
      .test("no-trailing-slash", TRAILING_SLASH_MESSAGE, noTrailingSlash),
    resetPasswordPage: yup.string().trim().required(),
    loginPage: yup.string().trim().required(),
    coursePaymentsPage: yup.string().trim().required().test("course-id", COURSE_ID_MESSAGE, containsCourseId),
    courseSchedulePage: yup.string().trim().required().test("course-id", COURSE_ID_MESSAGE, containsCourseId),
    courseExamsPage: yup.string().trim().required().test("course-id", COURSE_ID_MESSAGE, containsCourseId),
    settingsPage: yup.string().trim().required(),
  })
  .required();

export type ClientAppSettingsValues = yup.InferType<typeof schema>;
